import { createRoot } from 'react-dom/client';
import * as React from 'react';
import {
    createFetchProductApiHandler,
    resetVariantSelection
} from 'PyzShopUi/scripts/product-detail-page/includes/variant-helper/selectFilter';
import EventBus from 'PyzShopUi/scripts/utils/eventBus';
import MiddlewareEventBus from 'PyzShopUi/scripts/react/common/lib/MiddlewareEventBus';
import { BikeSelectEvent } from 'PyzShopUi/scripts/react/common/events/bikeSelectEvent';
import IReactEvent from 'PyzShopUi/scripts/interfaces/article-list/IReactEvent';
import FormUtils from 'PyzShopUi/scripts/utils/formUtils';
import ProductVariantDataHandler from 'PyzShopUi/scripts/product-detail-page/includes/productVariantData';
import { addFilterBikeParameter } from 'PyzShopUi/scripts/product-detail-page/includes/productDetailPageUrlParamHelper';
import ProductBikeSearchSuggestion, {
    getBikeDisplayName
} from 'PyzShopUi/scripts/product-detail-page/components/ProductBikeSearchSuggestion';
import Utils from 'PyzShopUi/scripts/utils/utils';

const searchValueMinLength = 2;
const bikeVariantDropDownSelector = '.bike-variant-dropdown[data-type="bike-search"]';

export default class ProductBikeSearchSuggestionWidget {
    private static searchSuggestionElementRoot;

    public static init = (articleMainInfoElement: HTMLElement, initProductInfoFunction: () => void): void => {
        const searchFormElement: HTMLFormElement = articleMainInfoElement.querySelector('#product-bike-search');
        const searchSuggestionElement: HTMLElement = articleMainInfoElement.querySelector('#bike-search-suggestion');

        if (!searchSuggestionElement || !searchFormElement) {
            return;
        }

        const productVariantApiUrl: string = searchSuggestionElement.dataset.productVariantApiUrl;
        const productBikeSearchApiUrl: string = searchSuggestionElement.dataset.productBikeSearchApiUrl;
        const abstractSku: string = searchSuggestionElement.dataset.abstractSku;
        const fetchProductApi = createFetchProductApiHandler(productVariantApiUrl);
        const bikeSearchInput: HTMLInputElement = searchFormElement.querySelector('input.search');

        const openSuggestions = (): void => {
            const isVisible: boolean = bikeSearchInput.value.length >= searchValueMinLength;
            render(isVisible);
        };

        MiddlewareEventBus.getInstance().subscribe((event: IReactEvent<any>) => {
            if (event.type !== BikeSelectEvent.SELECT_MY_BIKE) {
                return;
            }

            FormUtils.setInputFieldValue(bikeSearchInput, '');
            close();
        });

        const openSuggestionsDebounced = Utils.debounce(openSuggestions, 200, false);
        bikeSearchInput.addEventListener('input', () => {
            resetBikeSelection();
            render(false);
            openSuggestionsDebounced();
        });

        bikeSearchInput.addEventListener('focus', openSuggestions);
        document.addEventListener('click', (event: MouseEvent): void => {
            if (searchFormElement && !searchFormElement.contains(event.target as Node)
                && searchSuggestionElement && !searchSuggestionElement.contains(event.target as Node)) {
                close();
            }
        });

        const selectCallback = (): void => {
            EventBus.getInstance().publish('MyBikeRadioButtonHandler:deselect');
        };

        const close = (): void => {
            render(false);
        };

        const resetBikeSelection = () => {
            MiddlewareEventBus.getInstance().publish({
                type: BikeSelectEvent.SELECT_SEARCH_SUGGEST_BIKE,
                payload: {
                    options: null,
                    domEntryElement: document.querySelector(bikeVariantDropDownSelector),
                },
            });

            const addToCartFormElement: HTMLFormElement = document.querySelector('add-to-cart-form');
            resetVariantSelection(addToCartFormElement);
        };

        if (!this.searchSuggestionElementRoot) {
            this.searchSuggestionElementRoot = createRoot(searchSuggestionElement);
        }

        const render = (isVisible: boolean): void => this.searchSuggestionElementRoot.render(
            <ProductBikeSearchSuggestion
                productBikeSearchApiUrl={productBikeSearchApiUrl}
                onSelectBike={selectCallback}
                onClose={close}
                isVisible={isVisible}
                onClickHandler={selectBikeHandler}
                searchValue={bikeSearchInput.value}
                abstractSku={abstractSku}
            />
        );

        const selectBikeHandler: (selectedConcreteId: number, bike?: any) => void = (selectedConcreteId: number, bike: any) => {
            bikeSearchInput.value = getBikeDisplayName(bike);

            if (bike.bike_code) {
                addFilterBikeParameter(bike.bike_code.toString());
            }

            if (bike.products.length > 1) {
                const productVariantData = ProductVariantDataHandler.init();
                const concreteName = (product) => productVariantData[product.concrete_id.toString()].fullName;

                MiddlewareEventBus.getInstance().publish({
                    type: BikeSelectEvent.SELECT_SEARCH_SUGGEST_BIKE,
                    payload: {
                        options: bike.products.map((product) => ({
                            value: product.concrete_id.toString(),
                            label: product.bike_association ? `${concreteName(product)} [${product.bike_association}]` : concreteName(product),
                        })),
                        bikeCode: bike.bike_code,
                        domEntryElement: document.querySelector(bikeVariantDropDownSelector),
                    },
                });
            } else {
                MiddlewareEventBus.getInstance().publish({
                    type: BikeSelectEvent.SELECT_SEARCH_SUGGEST_BIKE,
                    payload: {
                        options: null,
                        bikeCode: bike.bike_code,
                        domEntryElement: document.querySelector(bikeVariantDropDownSelector),
                    },
                });
                fetchProductApi(selectedConcreteId);
            }
        };

        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);

        if (bikeSearchInput.value || (urlParams.has('filter_article_number') && !bikeSearchInput.value)) {
            const addToCartFormElement: HTMLFormElement = document.querySelector('add-to-cart-form');
            resetVariantSelection(addToCartFormElement);
            initProductInfoFunction();
        }
    };
}
